import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ROLE_LABEL } from "@/lib/roles";

const EMPTY = { name: "", email: "", role: "staff", doctor_id: "", temp_password: "" };
const genPassword = () => `Shri${Math.random().toString(36).slice(2, 8)}${Math.floor(100 + Math.random() * 900)}`;

export default function UserFormDialog({ open, onOpenChange, editing, doctors, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { name: editing.name || "", email: editing.email, role: editing.role, doctor_id: editing.doctor_id || "", temp_password: "" }
                    : { ...EMPTY, temp_password: genPassword() });
  }, [open, editing]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Enter a name");
    if (form.role === "doctor" && !form.doctor_id) return toast.error("Select the doctor profile for this user");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/users/${editing.id}`, { name: form.name, role: form.role, doctor_id: form.role === "doctor" ? form.doctor_id : null });
        toast.success("User updated");
      } else {
        await api.post("/users", { ...form, doctor_id: form.role === "doctor" ? form.doctor_id : null });
        toast.success(`User created. Temporary password: ${form.temp_password}`, { duration: 15000 });
      }
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(apiError(e, "Could not save user")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">{editing ? "Edit User" : "Add User"}</DialogTitle>
          <DialogDescription>{editing ? "Update role or linked doctor." : "They will be asked to set their own password on first login."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input data-testid="user-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Priya Patel" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editing} onChange={(e) => set("email", e.target.value)} placeholder="user@clinic.com" />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.role === "doctor" && (
            <div className="space-y-2">
              <Label>Linked Doctor Profile</Label>
              <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}>
                <SelectTrigger data-testid="user-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialty}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!editing && (
            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <div className="flex gap-2">
                <Input data-testid="user-temp-password-input" value={form.temp_password} onChange={(e) => set("temp_password", e.target.value)} className="font-mono" />
                <Button type="button" variant="outline" onClick={() => set("temp_password", genPassword())}>Generate</Button>
              </div>
              <p className="text-xs text-slate-500">Share this with the user. Min 10 characters with letters and numbers.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="user-form-submit" onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Create User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
